import React, { useEffect, useState } from 'react';
import { string, object, bool, array, number } from 'prop-types';
import { differenceInMilliseconds } from 'date-fns';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';

import Typography from '../Typography/Typography';

import messages from './messages';

export const MINUTE = 60_000;
export const FIVE_MINUTES = 5 * MINUTE;

const styles = {
  minutes: {
    fontSize: 14,
    lineHeight: 1,
    marginTop: 2,
  },
  numberL: {
    fontSize: 20,
    fontWeight: 'bold',
    lineHeight: 1,
    marginBottom: 2,
  },
  number: {
    fontSize: 50,
    lineHeight: 1,
    marginBottom: 4,
  },
};

const stylesInline = {
  active: { color: 'green' },
  inactive: { color: 'grey' },
};

const MyCircularProgress = ({ className, style, size = 'medium', time, live, active, range }) => {
  const [chrono, setChrono] = useState(0);
  const [chronoEnd, setChronoEnd] = useState(0);

  const isRange = Array.isArray(range) && range.length === 2;
  const intervalMs = isRange ? FIVE_MINUTES : MINUTE;

  useEffect(() => {
    let mounted = true;

    if (isRange) {
      setChrono(range[0]);
      setChronoEnd(range[1]);
      // eslint-disable-next-line eqeqeq
    } else if (time != null) {
      const initial = typeof time === 'number' ? time : Math.ceil(differenceInMilliseconds(time, new Date()) / MINUTE);
      setChrono(initial);
    }

    const timer = setInterval(() => {
      if (!mounted) {
        return;
      }
      if (isRange) {
        setChrono(c => Math.max(0, c - 5));
        setChronoEnd(e => Math.max(0, e - 5));
      } else {
        setChrono(c => Math.max(0, c - 1));
      }
    }, intervalMs);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [time, range, intervalMs, isRange]);

  const isRangeZero = isRange && chrono <= 0 && chronoEnd <= 0;
  const isSingleZero = !isRange && chrono <= 0;

  const displayValue = isRange ? `${Math.max(0, chrono)}-${Math.max(0, chronoEnd)}` : `${Math.max(0, chrono)}`;

  const numberStyle = (() => {
    if (isRange) {
      return { fontSize: 16, fontWeight: 'bold', lineHeight: 1.5 };
    }
    if (size === 'little') {
      return styles.numberL;
    }
    return styles.number;
  })();

  const colorStyle = live && active ? stylesInline.active : stylesInline.inactive;

  // eslint-disable-next-line eqeqeq
  const shouldShow = (time != null && !isRange) || isRange;

  return (
    <Grid name="CircularProgress" className={className} style={{ height: size === 'little' ? 70 : 160, ...style }}>
      <CircularProgress size={size === 'little' ? 70 : 160} thickness={2} style={live ? colorStyle : {}} />
      {shouldShow && (
        <Grid
          container
          direction="column"
          alignItems="center"
          style={{ position: 'relative', top: size === 'little' ? -58 : -140 }}
          data-testid={isRange ? 'range' : 'chrono'}
        >
          {isRangeZero || isSingleZero ? (
            <>
              <Typography style={{ ...(size === 'little' ? styles.numberL : styles.number), ...colorStyle }} color="primary" content={0} />
              <Typography style={{ ...styles.minutes, ...colorStyle }} color="primary" content={messages.minutes} />
            </>
          ) : (
            <>
              <Typography style={{ ...numberStyle, ...colorStyle }} color="primary" content={displayValue} />
              <Typography style={{ ...styles.minutes, ...colorStyle }} color="primary" content={messages.minutes} />
            </>
          )}
        </Grid>
      )}
    </Grid>
  );
};

MyCircularProgress.propTypes = {
  className: string,
  style: object,
  size: string,
  time: object || number,
  live: bool,
  active: bool,
  range: array,
};

export default MyCircularProgress;
